import { useEffect, Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { QuoteListProvider } from "@/contexts/QuoteListContext";
import { ChatProvider } from "@/contexts/ChatContext";

// Critical path components - load immediately
import Index from "./pages/Index";

// Lazy load non-critical pages for better initial load
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const QuoteRequest = lazy(() => import("./pages/QuoteRequest"));
const Auth = lazy(() => import("./pages/Auth"));
const MyCotizaciones = lazy(() => import("./pages/MyCotizaciones"));
const Profile = lazy(() => import("./pages/Profile"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin pages (separate chunk)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminProductEditor = lazy(() => import("./pages/admin/AdminProductEditor"));
const AdminProductImport = lazy(() => import("./pages/admin/AdminProductImport"));
const AdminMedia = lazy(() => import("./pages/admin/AdminMedia"));
const AdminQuotes = lazy(() => import("./pages/admin/AdminQuotes"));
const AdminFeaturedProducts = lazy(() => import("./pages/admin/AdminFeaturedProducts"));
const AdminDocumentos = lazy(() => import("./pages/admin/AdminDocumentos"));

// Lazy load chat assistant (not needed immediately)
const ChatAssistant = lazy(() => import("./components/chat/ChatAssistant").then(m => ({ default: m.ChatAssistant })));

// Lazy load ProtectedRoute
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Component to scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  
  return null;
}

const AppRoutes = () => (
  <>
    <ScrollToTop />
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/productos" element={<Products />} />
        <Route path="/productos/:slug" element={<ProductDetail />} />
        <Route path="/sobre-nosotros" element={<AboutUs />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/cotizacion" element={<QuoteRequest />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/mis-cotizaciones" element={
          <ProtectedRoute>
            <MyCotizaciones />
          </ProtectedRoute>
        } />
        <Route path="/perfil" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requiredRole="admin">
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="products/new" element={<AdminProductEditor />} />
          <Route path="products/:id/edit" element={<AdminProductEditor />} />
          <Route path="products/import" element={<AdminProductImport />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="featured" element={<AdminFeaturedProducts />} />
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="blog/new" element={<AdminBlogEditor />} />
          <Route path="blog/:id/edit" element={<AdminBlogEditor />} />
          <Route path="media" element={<AdminMedia />} />
          <Route path="cotizaciones" element={<AdminQuotes />} />
          <Route path="documentos" element={<AdminDocumentos />} />
        </Route>
        
        <Route path="/contacto" element={<Index />} />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    {/* Lazy load chat assistant after initial render */}
    <Suspense fallback={null}>
      <ChatAssistant />
    </Suspense>
  </>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <QuoteListProvider>
            <ChatProvider>
              <AppRoutes />
            </ChatProvider>
          </QuoteListProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
